import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, IconButton } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import useThemeStyles from '../../hooks/useThemeStyles';

const ReusableModal = ({
  open,
  onClose,
  title,
  titleAlign,
  children,
  width,
  keepMounted,
  showCloseButton,
  containerSx,
  contentSx,
  titleSx,
  closeButtonSx,
  ariaLabelledby,
  ariaDescribedby,
  disableAutoFocus,
}) => {
  const { colors, components, isDark } = useThemeStyles();

  // MUI components inside modals (Stepper, Chip, Checkbox, Typography
  // color="text.secondary", ...) otherwise render with MUI's default LIGHT
  // palette, which is unreadable on the dark modal background. Scope a MUI
  // theme that mirrors the app theme so every modal is legible in both modes.
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: { main: colors?.interactive?.primary || '#3b82f6' },
          background: {
            paper: isDark ? '#1a1a2e' : '#ffffff',
            default: isDark ? '#1a1a2e' : '#ffffff',
          },
          ...(colors?.text?.primary && colors?.text?.secondary
            ? {
                text: {
                  primary: colors.text.primary,
                  secondary: colors.text.secondary,
                },
              }
            : {}),
          ...(colors?.border?.subtle ? { divider: colors.border.subtle } : {}),
        },
      }),
    [isDark, colors]
  );

  const containerStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width,
    maxWidth: '92vw',
    maxHeight: '90vh',
    // Match LandingPage section background:
    // dark: #1a1a2e (rgb(26, 26, 46)), light: white
    backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
    borderRadius: 1,
    boxShadow: components?.paper?.shadow ?? 24,
    border: components?.paper?.border ?? `1px solid ${colors?.border?.subtle ?? 'divider'}`,
    px: 4,
    pt: 3,
    pb: 4,
    color: colors?.text?.primary ?? 'text.primary',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const primaryAccent =
    colors?.icon?.primary ||
    colors?.primary?.main ||
    colors?.sidebar?.headerHover ||
    colors?.sidebar?.headerBackground ||
    'primary.main';

  return (
    <ThemeProvider theme={muiTheme}>
      <Modal
        open={open}
        onClose={onClose}
        keepMounted={keepMounted}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        disableAutoFocus={disableAutoFocus}
      >
        <Box sx={{ ...containerStyles, ...(containerSx || {}) }}>
          {showCloseButton && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mb: title ? 1 : 2,
              }}
            >
              <IconButton
                aria-label="Close modal"
                onClick={onClose}
                size="small"
                sx={{
                  color: colors?.icon?.primary ?? colors?.text?.primary ?? 'text.primary',
                  ...(closeButtonSx || {}),
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {title && (
            <Typography
              variant="h6"
              sx={{
                color: primaryAccent,
                textAlign: titleAlign,
                mb: 3,
                ...(titleSx || {}),
              }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              ...(contentSx || {}),
            }}
          >
            {children}
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

ReusableModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  titleAlign: PropTypes.oneOf(['left', 'center', 'right']),
  children: PropTypes.node.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  keepMounted: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  containerSx: PropTypes.shape({}),
  contentSx: PropTypes.shape({}),
  titleSx: PropTypes.shape({}),
  closeButtonSx: PropTypes.shape({}),
  ariaLabelledby: PropTypes.string,
  ariaDescribedby: PropTypes.string,
  disableAutoFocus: PropTypes.bool,
};

ReusableModal.defaultProps = {
  title: '',
  titleAlign: 'center',
  width: 420,
  keepMounted: false,
  showCloseButton: false,
  containerSx: {},
  contentSx: {},
  titleSx: {},
  closeButtonSx: {},
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
  disableAutoFocus: false,
};

export default ReusableModal;
